import { useState, useEffect, memo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import * as api from '../../services/api';

export const PageSetupPassword = memo(({ onLogin }) => {
  const { token = '' } = useParams();
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [vis, setVis] = useState(false);

  useEffect(() => {
    document.title = 'GOtaps - Setup Password';
    setTimeout(() => setVis(true), 80);

    if (!token) {
      setError('Invalid or missing setup link. Please use the link from your email.');
      setValidating(false);
      return;
    }

    // Validate the token first
    api.validateSetPasswordToken(token)
      .then(res => {
        setTokenValid(true);
        setUserInfo({ name: res.name, email: res.email });
      })
      .catch(e => {
        setError(e.message || 'Invalid or expired token. Please contact your admin.');
      })
      .finally(() => setValidating(false));
  }, [token]);

  const handleSubmit = async () => {
    setError('');
    if (!password.trim()) { setError('Please enter a password.'); return; }
    if (password.length < 6) { setError('Password must be at least 6 characters.'); return; }
    if (password !== confirm) { setError('Passwords do not match.'); return; }

    setLoading(true);
    try {
      const res = await api.setupPassword(token, password, confirm);

      // Backend doesn't return a token — auto-login with email + new password
      try {
        const loginRes = await api.login(userInfo.email, password);
        const userData = loginRes.data || { name: userInfo.name, email: userInfo.email };
        onLogin(userData, loginRes.token, true);
      } catch {
        // If auto-login fails, redirect to login page
        setSuccess('Password set successfully! Redirecting to sign in...');
        setTimeout(() => navigate('/login'), 2000);
      }
    } catch (e) {
      setError(e.message || 'Failed to setup password. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  const handleKey = e => { if (e.key === 'Enter') handleSubmit(); };

  return (
    <>
      <style>{`
        @keyframes orbDrift1{0%,100%{transform:translate(0,0) scale(1)}50%{transform:translate(38px,-28px) scale(1.07)}}
        @keyframes orbDrift2{0%,100%{transform:translate(0,0)}50%{transform:translate(-32px,22px) scale(1.05)}}
        @keyframes orbDrift3{0%,100%{transform:translate(0,0)}50%{transform:translate(22px,18px)}}
        @keyframes borderTrail{0%{background-position:0%}100%{background-position:200%}}
        .si-card::before{content:'';position:absolute;top:0;left:0;right:0;height:1.5px;background:linear-gradient(90deg,transparent,rgba(100,116,139,.5),rgba(212,175,55,.45),rgba(100,116,139,.5),transparent);background-size:200%;animation:borderTrail 4s linear infinite;border-radius:30px 30px 0 0}
      `}</style>

      {/* Ambient orbs */}
      <div className="si-scene">
        <div className="si-orb si-orb-1"/>
        <div className="si-orb si-orb-2"/>
        <div className="si-orb si-orb-3"/>
        <div className="si-orb si-orb-4"/>
        <div className="si-noise"/>
      </div>

      {/* Center card */}
      <div style={{
        position:"fixed", inset:0, zIndex:10,
        display:"flex", alignItems:"center", justifyContent:"center",
        padding:"20px", pointerEvents:"none",
      }}>
        <div style={{
          pointerEvents:"all",
          opacity:vis?1:0,
          transform:vis?"none":"translateY(20px) scale(.97)",
          transition:"opacity .55s ease 0s, transform .55s cubic-bezier(.16,1,.3,1) 0s",
        }}>
          <div className="si-card">
            <div className="si-logo">
              <img src={process.env.PUBLIC_URL + '/logo.png'} alt="GOtaps" style={{height:40,objectFit:'contain'}}/>
              <div className="si-logo-text">
                <span className="si-logo-tag">Enterprise</span>
              </div>
            </div>

            <div className="si-title">Setup Your Password</div>
            <div className="si-sub">
              {validating ? 'Validating your link...' :
               userInfo ? <>Welcome, <b style={{color:'#0f172a'}}>{userInfo.name}</b>. Create a secure password for <b style={{color:'#0f172a'}}>{userInfo.email}</b></> :
               'Create a secure password to access your workspace'}
            </div>

            {error && <div className="si-error"><span>⚠️</span> {error}</div>}
            {success && <div className="si-success"><span>✅</span> {success}</div>}

            {validating && (
              <div style={{display:'flex',justifyContent:'center',padding:'20px 0'}}>
                <div className="si-spinner" style={{width:24,height:24,borderColor:'rgba(100,116,139,.2)',borderTopColor:'#64748b'}}/>
              </div>
            )}

            {tokenValid && !success && (
              <>
                <div className="si-inp-wrap">
                  <span className="si-inp-ico">🔒</span>
                  <input
                    className="si-inp"
                    type={showPw ? "text" : "password"}
                    placeholder="New password"
                    value={password}
                    onChange={e => { setPassword(e.target.value); setError(''); }}
                    onKeyDown={handleKey}
                    style={{paddingRight:44}}
                  />
                  <button className="si-inp-toggle" tabIndex={-1} onClick={() => setShowPw(v => !v)}>
                    {showPw ? "🙈" : "👁"}
                  </button>
                </div>

                <div className="si-inp-wrap">
                  <span className="si-inp-ico">🔒</span>
                  <input
                    className="si-inp"
                    type="password"
                    placeholder="Confirm password"
                    value={confirm}
                    onChange={e => { setConfirm(e.target.value); setError(''); }}
                    onKeyDown={handleKey}
                  />
                </div>

                {/* Password strength indicator */}
                {password && (
                  <div style={{marginBottom:14}}>
                    <div style={{display:'flex',gap:4,marginBottom:4}}>
                      {[1,2,3,4].map(i => {
                        const strength = (password.length >= 6 ? 1 : 0) + (password.length >= 8 ? 1 : 0) + (/[A-Z]/.test(password) && /[a-z]/.test(password) ? 1 : 0) + (/\d/.test(password) || /[^a-zA-Z0-9]/.test(password) ? 1 : 0);
                        const colors = ['#dc2626','#d97706','#0891b2','#059669'];
                        return <div key={i} style={{flex:1,height:3,borderRadius:2,background:i<=strength?colors[strength-1]:'rgba(0,0,0,.08)',transition:'all .3s'}}/>;
                      })}
                    </div>
                    <div style={{fontSize:10,color:'#94a3b8'}}>
                      {password.length < 6 ? 'Too short' : password.length < 8 ? 'Fair' : 'Strong'}
                    </div>
                  </div>
                )}

                <button className="si-btn" disabled={loading} onClick={handleSubmit} style={{display:"flex",alignItems:"center",justifyContent:"center",gap:9}}>
                  {loading ? <><div className="si-spinner"/>Setting up...</> : <><span>Setup Password & Login</span><span style={{opacity:.7,fontSize:16}}>→</span></>}
                </button>
              </>
            )}

            {!validating && !tokenValid && (
              <div className="si-footer" style={{marginTop:14}}>
                <button className="si-footer-link" onClick={() => navigate('/login')}>← Back to sign in</button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
});
